/**
 * FacebookCAPIEditor - Configuration editor for Facebook Conversion API (Server-Side)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Switch,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import type { FacebookCAPIConfig, PlatformConfig } from '../../types/analytics.types';
import {
  getProjectAnalyticsService,
  type FacebookCAPITestResult,
} from '../../services/ProjectAnalyticsService';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

interface FacebookCAPIEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
  projectId?: string;
}

export const FacebookCAPIEditor: React.FC<FacebookCAPIEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
  projectId,
}) => {
  const fbConfig = config as FacebookCAPIConfig;
  const [localConfig, setLocalConfig] = useState<FacebookCAPIConfig>(fbConfig);
  const [localName, setLocalName] = useState(name || '');

  // Test event state
  const [isSending, setIsSending] = useState(false);
  const [testResult, setTestResult] = useState<FacebookCAPITestResult | null>(null);

  // Track if we've auto-generated testEventCode
  const hasAutoGeneratedCode = useRef(false);

  useEffect(() => {
    setLocalConfig(fbConfig);
    setLocalName(name || '');
  }, [fbConfig, name]);

  // Auto-generate testEventCode when pixelId is first set
  useEffect(() => {
    if (
      localConfig.pixelId &&
      !localConfig.testEventCode &&
      !hasAutoGeneratedCode.current
    ) {
      const defaultCode = `TEST${Date.now().toString().slice(-8)}`;
      const newConfig = { ...localConfig, testEventCode: defaultCode };
      setLocalConfig(newConfig);
      hasAutoGeneratedCode.current = true;
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    }
  }, [localConfig.pixelId, localConfig.testEventCode, localName, onChange, showSaveButton, localConfig]);

  const handleChange = useCallback(
    (field: keyof FacebookCAPIConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  const handleSendTestEvent = useCallback(async () => {
    if (!projectId || !localConfig.pixelId || !localConfig.accessToken || !localConfig.testEventCode) {
      setTestResult({
        success: false,
        error: 'Missing required fields: Pixel ID, Access Token, and Test Event Code',
      });
      return;
    }

    setIsSending(true);
    setTestResult(null);

    try {
      const service = getProjectAnalyticsService();
      const result = await service.sendFacebookCAPITestEvent({
        projectId,
        pixelId: localConfig.pixelId,
        accessToken: localConfig.accessToken,
        testEventCode: localConfig.testEventCode,
        eventName: 'Test Segmently Integration',
      });
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSending(false);
    }
  }, [projectId, localConfig.pixelId, localConfig.accessToken, localConfig.testEventCode]);

  const canSendTest = Boolean(
    projectId &&
    localConfig.pixelId &&
    localConfig.accessToken &&
    localConfig.testEventCode &&
    !disabled &&
    !readOnly &&
    !isSending
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Facebook Conversion API sends purchase events server-side for better attribution accuracy.
        This is separate from the client-side Facebook Pixel.
      </Alert>

      <TextField
        label="Pixel ID"
        value={localConfig.pixelId || ''}
        onChange={(e) => handleChange('pixelId', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="123456789012345"
        helperText={
          <>
            Same Pixel ID as your client-side Facebook Pixel. Find it in{' '}
            <Link
              href="https://business.facebook.com/events_manager2/list/pixel"
              target="_blank"
              rel="noopener"
            >
              Meta Events Manager
            </Link>
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('facebook_capi', 'pixelId') } }}
      />

      <TextField
        label="System User Access Token"
        value={localConfig.accessToken || ''}
        onChange={(e) => handleChange('accessToken', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        type="password"
        sx={{ mb: 2 }}
        placeholder="EAAxxxxxxxxxxxxxxxxx..."
        helperText={
          <>
            Create a System User in{' '}
            <Link
              href="https://business.facebook.com/settings/system-users"
              target="_blank"
              rel="noopener"
            >
              Business Settings → System Users
            </Link>
            {' and generate a token with ads_management and ads_read permissions.'}
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('facebook_capi', 'accessToken') } }}
      />

      {/* Test Event Code with Send Test button */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <TextField
          label="Test Event Code"
          value={localConfig.testEventCode || ''}
          onChange={(e) => handleChange('testEventCode', e.target.value)}
          disabled={disabled || readOnly}
          fullWidth
          placeholder="TEST12345678"
          helperText={
            <>
              Required for testing. Get code from{' '}
              <Link
                href="https://business.facebook.com/events_manager2/list/pixel"
                target="_blank"
                rel="noopener"
              >
                Meta Events Manager → Test Events
              </Link>
            </>
          }
        />
        <Button
          variant="outlined"
          onClick={handleSendTestEvent}
          disabled={!canSendTest}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ mt: 1, minWidth: 130, whiteSpace: 'nowrap' }}
        >
          {isSending ? 'Sending...' : 'Send Test'}
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localConfig.renewalsAsPurchase ?? true}
              onChange={(e) => handleChange('renewalsAsPurchase', e.target.checked)}
              disabled={disabled || readOnly}
            />
          }
          label="Send subscription renewals as Purchase"
        />
        <FormHelperText sx={{ ml: 0 }}>
          ON: renewals inflate Purchase/ROAS but feed value optimization. OFF: renewals arrive as
          custom &quot;RecurringPayment&quot; event (same user matching, excluded from Purchase counts).
        </FormHelperText>
      </Box>

      {/* Test Result Alert */}
      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.success
            ? `Test event sent successfully! Events received: ${testResult.eventsReceived || 1}. Check Meta Events Manager → Test Events.`
            : `Error: ${testResult.error}`}
        </Alert>
      )}

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.pixelId || !localConfig.accessToken}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('facebook_capi')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
